"use client"

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Handle, Position, useNodes, useEdges } from "reactflow"
import { router } from "@inertiajs/react"
import { Mail, Minus, Plus, Trash2, AlertTriangle } from "lucide-react"
import "./Node.css"
import "./EmailNode.css"
import { useWorkflow } from "@/lib/contexts/workflow-context"
import CopyPasteButtons from "./CopyPasteButtons"
import EnhancedVariableReference from "./EnhancedVariableReference"
import { collectUpstreamFormFieldVariables, hasUpstreamTaskNode } from "@/lib/workflowFormFieldVariables"
import { getSystemVariablesForContext } from "@/lib/workflowSystemVariables"
import { renderPreviewContent, suggestVariableMapping } from "@/lib/textTemplateVariables"
import EmailRichTextEditor from "@/components/shared/EmailRichTextEditor"
import { buildWorkflowInsertVariables } from "@/lib/workflowEmailVariables"

const ensureString = (value) => (value === null || value === undefined ? "" : String(value))

const getSettingValue = (source, key, fallback = "") => {
  if (source?.[key] !== undefined && source?.[key] !== null) {
    return ensureString(source[key])
  }
  return ensureString(fallback)
}

const VAR_HINT = "Tip: use {{variableName}} to pull values from previous steps."

const EmailNode = ({ data, id, csrf_token, emailTemplates = [], emailAccounts = [], envEmailDefault = null }) => {
  const { currentWorkflow, updateNode } = useWorkflow()
  const { expanded = true } = data
  const settings = data?.settings || {}

  const [isHovering, setIsHovering] = useState(false)
  const [showMinimizeTooltip, setShowMinimizeTooltip] = useState(false)

  const nodes = useNodes()
  const edges = useEdges()

  const buildState = (src, srcSettings) => ({
    label: ensureString(src.label) || "Send Email",
    name: ensureString(src.name) || ensureString(src.label) || "Send Email",
    description: ensureString(src.description),
    templateId: ensureString(src.templateId ?? srcSettings?.template_id ?? ""),
    emailAccountId: (() => {
      const fromData = src.emailAccountId ?? srcSettings?.email_account_id
      if (fromData === null || fromData === undefined) {
        return ""
      }
      return ensureString(fromData)
    })(),
    recipientSource:
      ensureString(src.recipientSource) ||
      getSettingValue(srcSettings, "recipient_source", "variable") ||
      "variable",
    recipientEmail:
      ensureString(src.recipientEmail) || getSettingValue(srcSettings, "recipient_email", ""),
    variableMappings: src.variableMappings || srcSettings?.variable_mappings || {},
    messageOverride:
      ensureString(src.messageOverride) || getSettingValue(srcSettings, "message_override", ""),
    subjectOverride:
      ensureString(src.subjectOverride) || getSettingValue(srcSettings, "subject_override", ""),
    useTemplateMessage:
      typeof src.useTemplateMessage === "boolean"
        ? src.useTemplateMessage
        : (srcSettings?.use_template_message ?? true),
    useTemplateSubject:
      typeof src.useTemplateSubject === "boolean"
        ? src.useTemplateSubject
        : (srcSettings?.use_template_subject ?? true),
    expanded: src.expanded ?? true,
  })

  const [nodeData, setNodeData] = useState(() => buildState(data, settings))
  const nodeDataRef = useRef(nodeData)

  useEffect(() => {
    setNodeData(buildState(data, data?.settings || {}))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.label,
    data.name,
    data.description,
    data.templateId,
    data.emailAccountId,
    data.recipientSource,
    data.recipientEmail,
    data.variableMappings,
    data.messageOverride,
    data.subjectOverride,
    data.useTemplateMessage,
    data.useTemplateSubject,
    data.expanded,
    data.settings,
    id,
  ])

  useEffect(() => {
    nodeDataRef.current = nodeData
  }, [nodeData])

  const stepDatabaseId =
    data?.databaseId ??
    (typeof id === "string" && id.startsWith("step-") ? id.replace("step-", "") : null)

  const selectedTemplate = useMemo(
    () => emailTemplates.find((t) => String(t.id) === String(nodeData.templateId)),
    [emailTemplates, nodeData.templateId],
  )

  const getAvailableRequestVariables = useCallback(() => {
    if (!nodes || !edges) return []
    const availableVariables = []
    const visitedNodes = new Set()

    const findUpstreamRequestNodes = (nodeId) => {
      if (visitedNodes.has(nodeId)) return
      visitedNodes.add(nodeId)

      const node = nodes.find((n) => n.id === nodeId)
      if (!node) return

      if (
        (node.type === "requestNode" || node.type === "getNoteNode") &&
        Array.isArray(node.data?.extractedVariables)
      ) {
        availableVariables.push(...node.data.extractedVariables)
      }

      const incomingEdges = edges.filter((edge) => edge.target === nodeId)
      incomingEdges.forEach((edge) => findUpstreamRequestNodes(edge.source))
    }

    findUpstreamRequestNodes(id)
    return availableVariables.filter((v) => v && typeof v.name === "string")
  }, [nodes, edges, id])

  const getAvailableFormFields = useCallback(
    () => collectUpstreamFormFieldVariables(nodes, edges, id),
    [nodes, edges, id],
  )

  const systemVariables = useMemo(
    () =>
      getSystemVariablesForContext({
        hasUpstreamCompletedTask: hasUpstreamTaskNode(nodes, edges, id),
      }),
    [nodes, edges, id],
  )

  const getAvailableVariables = useCallback(
    () => [...getAvailableRequestVariables(), ...getAvailableFormFields()],
    [getAvailableRequestVariables, getAvailableFormFields],
  )

  const upstreamVariables = useMemo(() => getAvailableVariables(), [getAvailableVariables])

  const workflowInsertVariables = useMemo(
    () => buildWorkflowInsertVariables(upstreamVariables, systemVariables),
    [upstreamVariables, systemVariables],
  )

  const templateVariables = useMemo(() => {
    if (!selectedTemplate?.variables) return []
    return selectedTemplate.variables.map((v) => (typeof v === "string" ? { key: v } : v))
  }, [selectedTemplate])

  const renderTemplatePart = useCallback(
    (content) => {
      if (!content) return ""
      let rendered = content
      Object.entries(nodeData.variableMappings || {}).forEach(([slot, mappingValue]) => {
        const token = new RegExp(
          `\\{\\{\\s*${slot.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*\\}\\}`,
          "g",
        )
        rendered = rendered.replace(token, mappingValue || "")
      })
      return renderPreviewContent(rendered)
    },
    [nodeData.variableMappings],
  )

  const renderedSubject = useMemo(() => {
    if (!nodeData.useTemplateSubject && nodeData.subjectOverride) {
      return nodeData.subjectOverride
    }
    return renderTemplatePart(selectedTemplate?.subject || nodeData.subjectOverride || "")
  }, [nodeData, selectedTemplate, renderTemplatePart])

  const renderedMessage = useMemo(() => {
    if (!nodeData.useTemplateMessage && nodeData.messageOverride) {
      return nodeData.messageOverride
    }
    return renderTemplatePart(selectedTemplate?.content || nodeData.messageOverride || "")
  }, [nodeData, selectedTemplate, renderTemplatePart])

  const updateWorkflowContext = useCallback(
    (updatedData) => {
      if (currentWorkflow) {
        updateNode(currentWorkflow.id, id, { data: updatedData })
      }
    },
    [currentWorkflow, id, updateNode],
  )

  const persistData = useCallback(
    (updated) => {
      updateWorkflowContext(updated)
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, { ...updated, expanded })
      }
    },
    [updateWorkflowContext, data, id, expanded],
  )

  const handleChange = (field, value, options = {}) => {
    const { persistOnChange = false } = options
    const updated = {
      ...nodeDataRef.current,
      [field]: typeof value === "boolean" ? value : ensureString(value),
    }
    if (field === "name") {
      updated.label = ensureString(value) || "Send Email"
    }
    setNodeData(updated)
    if (persistOnChange) {
      persistData(updated)
    }
  }

  const handleTemplateChange = (templateId) => {
    const template = emailTemplates.find((t) => String(t.id) === String(templateId))
    const mappings = { ...(nodeDataRef.current.variableMappings || {}) }

    if (template?.variables) {
      template.variables.forEach((variable) => {
        const key = typeof variable === "string" ? variable : variable.key
        if (!mappings[key]) {
          const suggestion = suggestVariableMapping(key, upstreamVariables)
          if (suggestion) mappings[key] = suggestion
        }
      })
    }

    const updated = {
      ...nodeDataRef.current,
      templateId: ensureString(templateId),
      variableMappings: mappings,
      useTemplateMessage: true,
      useTemplateSubject: true,
      messageOverride: "",
      subjectOverride: "",
      emailAccountId:
        template?.email_account_id
          ? String(template.email_account_id)
          : nodeDataRef.current.emailAccountId ?? "",
    }
    setNodeData(updated)
    persistData(updated)
  }

  const handleMappingChange = (slot, value) => {
    const updated = {
      ...nodeDataRef.current,
      variableMappings: {
        ...(nodeDataRef.current.variableMappings || {}),
        [slot]: value,
      },
    }
    setNodeData(updated)
  }

  const handleBlur = () => {
    const nodeId = id
    const persist = data.onNodeDataChange
    setTimeout(() => {
      const current = nodeDataRef.current
      if (!current) return
      updateWorkflowContext(current)
      if (persist) {
        persist(nodeId, { ...current, expanded })
      }
    }, 0)
  }

  const toggleMinimize = () => {
    if (data.onNodeStateChange) {
      data.onNodeStateChange(id, !expanded)
    }
  }

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this node?")) {
      return
    }

    if (!currentWorkflow?.id || !stepDatabaseId) {
      console.error("Send Email node: cannot delete -- missing workflow or step database id")
      return
    }

    router.delete(`/workflows/${currentWorkflow.id}/steps/${stepDatabaseId}`, {
      preserveState: true,
      preserveScroll: true,
      only: [],
      onSuccess: () => {
        if (typeof data.onDeleteNode === "function") {
          data.onDeleteNode(id)
        }
      },
      onError: (errors) => {
        console.error("Send Email node: failed to delete step via Inertia", errors)
        alert("Failed to delete node. Please try again.")
      },
    })
  }

  useEffect(() => {
    const handleWheel = (e) => {
      const nodeContent = e.target.closest(".node-content")
      if (!nodeContent) return
      if (!e.target.closest(".email-node-outline")) return
      if (e.ctrlKey || e.shiftKey) return
      e.stopPropagation()
    }
    document.addEventListener("wheel", handleWheel, { passive: false, capture: true })
    return () => {
      document.removeEventListener("wheel", handleWheel, { capture: true })
    }
  }, [id, expanded])

  const handleUseTemplateMessageChange = (checked) => {
    const current = nodeDataRef.current
    const templateBody = renderTemplatePart(
      selectedTemplate?.content || current.messageOverride || "",
    )

    const updated = {
      ...current,
      useTemplateMessage: checked,
      ...(!checked
        ? { messageOverride: current.messageOverride || templateBody }
        : {}),
    }
    setNodeData(updated)
    persistData(updated)
  }

  const inputClass =
    "w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500 nodrag"

  const templateMissing = nodeData.templateId && !selectedTemplate
  const templateInactive = selectedTemplate && !selectedTemplate.is_active

  const subjectPreviewValue = nodeData.useTemplateSubject
    ? renderedSubject
    : nodeData.subjectOverride || renderedSubject

  const messagePreviewValue = nodeData.useTemplateMessage
    ? renderedMessage
    : nodeData.messageOverride || renderedMessage

  return (
    <div
      className={`trigger-node-container email-node-outline dark:bg-card ${!expanded ? "minimized" : ""} ${isHovering ? "email-node-hovering" : ""}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="trigger-node-header dark:bg-card dark:border-border">
        <div className="trigger-node-title">
          <Mail size={16} className="trigger-icon" />
          <span>
            {nodeData.name && nodeData.name.trim() !== "" ? nodeData.name : "Send Email"}
          </span>
        </div>
        <div className="trigger-node-controls">
          <CopyPasteButtons
            nodeId={id}
            nodeType="sendEmailNode"
            nodeName={nodeData.name}
            className="nodrag"
          />
          <div className="minimize-button-container nodrag">
            <button
              className="trigger-node-button nodrag dark:hover:bg-card dark:hover:text-white"
              onClick={(e) => {
                e.stopPropagation()
                toggleMinimize()
              }}
              onMouseEnter={() => setShowMinimizeTooltip(true)}
              onMouseLeave={() => setShowMinimizeTooltip(false)}
            >
              {!expanded ? <Plus size={16} /> : <Minus size={16} />}
            </button>
            {showMinimizeTooltip && <div className="minimize-tooltip"></div>}
          </div>
          <button
            className="trigger-node-button nodrag dark:hover:bg-card"
            onClick={(e) => {
              e.stopPropagation()
              handleDelete()
            }}
            title="Delete node"
            style={{ color: "#ef4444" }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {expanded && (
        <div
          className="node-content nowheel dark:bg-card dark:text-white dark:border-border"
          onWheel={(e) => e.stopPropagation()}
        >
          {(templateMissing || templateInactive) && (
            <div className="email-node-warning">
              <AlertTriangle size={12} />
              <span>
                {templateMissing
                  ? "Selected template is missing or unavailable."
                  : "Selected template is inactive."}
              </span>
            </div>
          )}

          <div className="node-field">
            <label className="dark:text-white">Node Name</label>
            <EnhancedVariableReference
              value={nodeData.name}
              onChange={(value) => handleChange("name", value)}
              onBlur={handleBlur}
              availableVariables={upstreamVariables}
              systemVariables={systemVariables}
              placeholder="Send Email"
              className={inputClass}
              showDateVariables={true}
            />
          </div>

          <div className="node-field">
            <label className="dark:text-white">Description</label>
            <EnhancedVariableReference
              value={nodeData.description}
              onChange={(value) => handleChange("description", value)}
              onBlur={handleBlur}
              availableVariables={upstreamVariables}
              systemVariables={systemVariables}
              placeholder="Send email via SMTP"
              className={inputClass}
              showDateVariables={true}
            />
          </div>

          <div className="node-field">
            <label className="dark:text-white">Email Account</label>
            <select
              value={nodeData.emailAccountId}
              onChange={(e) => handleChange("emailAccountId", e.target.value, { persistOnChange: true })}
              onBlur={handleBlur}
              className={inputClass}
            >
              <option value="">
                {envEmailDefault
                  ? `Environment default (${envEmailDefault.email})`
                  : "Default account"}
              </option>
              {emailAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name} ({account.email})
                  {account.is_default ? " — Default" : ""}
                </option>
              ))}
            </select>
            <div className="email-node-field-hint">
              {emailAccounts.length === 0 && envEmailDefault ? (
                <>
                  Using SMTP from <strong>.env</strong> ({envEmailDefault.email}
                  {envEmailDefault.smtp_host ? ` via ${envEmailDefault.smtp_host}` : ""}).{" "}
                  <a href="/settings?tab=email-accounts" className="text-blue-600 dark:text-blue-400 underline">
                    Add email accounts
                  </a>{" "}
                  to use different senders.
                </>
              ) : (
                <>
                  Accounts are managed under{" "}
                  <a href="/settings?tab=email-accounts" className="text-blue-600 dark:text-blue-400 underline">
                    Settings → Email Accounts
                  </a>
                  .
                </>
              )}
            </div>
          </div>

          <div className="node-field">
            <label className="dark:text-white">
              Template<span className="email-node-required">*</span>
            </label>
            <select
              value={nodeData.templateId}
              onChange={(e) => handleTemplateChange(e.target.value)}
              onBlur={handleBlur}
              className={inputClass}
            >
              <option value="">Select a template...</option>
              {emailTemplates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
            <div className="email-node-field-hint">
              Templates are managed under <strong>Admin &rarr; Email Templates</strong>.
            </div>
          </div>

          <div className="node-field">
            <label className="dark:text-white">
              Recipient Email
              <span className="email-node-required">*</span>
            </label>
            <EnhancedVariableReference
              value={nodeData.recipientEmail}
              onChange={(value) => handleChange("recipientEmail", value)}
              onBlur={handleBlur}
              availableVariables={upstreamVariables}
              systemVariables={systemVariables}
              placeholder="{{customer_email}}"
              className={inputClass}
              showDateVariables={true}
            />
            <div className="email-node-field-hint">{VAR_HINT}</div>
          </div>

          {templateVariables.length > 0 && (
            <div className="node-field">
              <label className="dark:text-white">Variable Mapping</label>
              <div className="email-node-mapping-table">
                {templateVariables.map((variable) => {
                  const key = variable.key
                  return (
                    <div key={key} className="email-node-mapping-row">
                      <code className="email-node-mapping-slot">{`{{ ${key} }}`}</code>
                      <EnhancedVariableReference
                        value={nodeData.variableMappings?.[key] || ""}
                        onChange={(value) => handleMappingChange(key, value)}
                        onBlur={handleBlur}
                        availableVariables={upstreamVariables}
                        systemVariables={systemVariables}
                        placeholder={`Map ${key}`}
                        className={inputClass}
                        showDateVariables={true}
                      />
                    </div>
                  )
                })}
              </div>
              <div className="email-node-field-hint">
                Map template slots to upstream variables or literal values.
              </div>
            </div>
          )}

          <div className="node-field email-node-checkbox-row">
            <input
              id={`email-node-use-template-subject-${id}`}
              type="checkbox"
              checked={Boolean(nodeData.useTemplateSubject)}
              onChange={(e) =>
                handleChange("useTemplateSubject", e.target.checked, { persistOnChange: true })
              }
              className="nodrag"
            />
            <label
              htmlFor={`email-node-use-template-subject-${id}`}
              className="dark:text-white"
              style={{ marginBottom: 0, cursor: "pointer" }}
            >
              Use template subject (uncheck to override)
            </label>
          </div>

          <div className="node-field">
            <label className="dark:text-white">Subject Preview / Override</label>
            <EnhancedVariableReference
              value={subjectPreviewValue}
              onChange={(value) => {
                const updated = {
                  ...nodeDataRef.current,
                  useTemplateSubject: false,
                  subjectOverride: value,
                }
                setNodeData(updated)
              }}
              onBlur={handleBlur}
              disabled={nodeData.useTemplateSubject}
              availableVariables={upstreamVariables}
              systemVariables={systemVariables}
              placeholder={
                nodeData.useTemplateSubject
                  ? "Select a template to preview the subject"
                  : "Override the template subject"
              }
              className={inputClass}
              showDateVariables={true}
            />
          </div>

          <div className="node-field email-node-checkbox-row">
            <input
              id={`email-node-use-template-${id}`}
              type="checkbox"
              checked={Boolean(nodeData.useTemplateMessage)}
              onChange={(e) => handleUseTemplateMessageChange(e.target.checked)}
              className="nodrag"
            />
            <label
              htmlFor={`email-node-use-template-${id}`}
              className="dark:text-white"
              style={{ marginBottom: 0, cursor: "pointer" }}
            >
              Use template body (uncheck to override)
            </label>
          </div>

          <div className="node-field">
            <label className="dark:text-white">Message Preview / Override</label>
            <EmailRichTextEditor
              className="email-node-rich-editor nodrag"
              value={messagePreviewValue}
              onChange={(value) => {
                const updated = {
                  ...nodeDataRef.current,
                  useTemplateMessage: false,
                  messageOverride: value,
                }
                setNodeData(updated)
              }}
              onBlur={handleBlur}
              disabled={nodeData.useTemplateMessage}
              variables={workflowInsertVariables}
              buttonVariant="primary"
              placeholder="Write your email message"
              minHeight={140}
            />
            <div className="email-node-meta">
              {(messagePreviewValue || "").replace(/<[^>]+>/g, "").length} character
              {(messagePreviewValue || "").replace(/<[^>]+>/g, "").length !== 1 ? "s" : ""}
            </div>
          </div>
        </div>
      )}

      <Handle type="target" position={Position.Top} id="b" />
      <Handle type="source" position={Position.Bottom} id="a" />
    </div>
  )
}

export default EmailNode
